import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useAuthentication } from "hooks/useAuthentication";
import { useContext, useMemo, useState } from "react";
import { Store } from "store";
import { showToast, toastTypes } from "utils";
import { AppDropdownSelect } from "components/AppDropdownSelect";
import AppInput from "components/AppInput";
import { uploadStudentsCsv } from "services/tracking/studentsService";

const validationSchema = Yup.object().shape({
  institution_id: Yup.string().required("Institution requise"),
  csv: Yup.mixed().required("Fichier CSV requis"),
});

export default function TrackingStudentsUploadPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { dispatch } = useContext(Store);
  const { user } = useAuthentication();
  const { getInstitutions } = useApi();
  const { data: institutions } = useCache("institutions", () => getInstitutions(1));
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const initialValues = useMemo(
    () => ({
      institution_id: user?.user?.institution_id ? String(user.user.institution_id) : "",
      csv: null as File | null,
    }),
    [user?.user]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("institution_id", values.institution_id);
        if (values.csv) {
          formData.append("csv_file", values.csv, values.csv.name);
        }

        const token = user?.auth?.access_token;
        await uploadStudentsCsv(token, formData);

        dispatch(
          showToast({
            toast,
            message:
              "Fichier élèves reçu. Le traitement sera effectué côté serveur (suivi à implémenter dans l'API).",
            status: toastTypes.success,
          })
        );
        navigate("/tracking/students");
      } catch (error: any) {
        const message =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Erreur lors de l'import des élèves (vérifiez que l'endpoint /api/v1/students/upload/ est disponible).";
        dispatch(
          showToast({
            toast,
            message,
            status: toastTypes.error,
          })
        );
      }
    },
  });

  const { values, setFieldValue, handleSubmit, isSubmitting, errors, touched } = formik;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviewSrc(url);
    setFieldValue("csv", file);
  };

  return (
    <TrackingPageLayout
      title="Import des élèves (CSV)"
      action={
        <Button
          size="xl"
          variant="outline"
          colorScheme="red_origin"
          fontWeight={700}
          borderRadius="8px"
          onClick={() => {
            const link = document.createElement("a");
            link.href = "/sample_students.csv";
            link.download = "sample_students.csv";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        >
          Télécharger un exemple
        </Button>
      }
    >
      <form onSubmit={handleSubmit}>
        <Flex flexDirection="column" alignItems="center">
          <Flex
            gap="16px"
            bg="white"
            boxShadow="xl"
            w="100%"
            flexDirection="column"
            px={{ base: "20px", sm: "24px" }}
            py={{ base: "20px", sm: "24px" }}
            mx={{ md: "62px", base: "0px" }}
            borderRadius="16px"
          >
            <Flex gap="8px" flexDirection="column" alignItems="start">
              <HStack>
                <Text color="gray_description" mt="16px">
                  Fichier CSV contenant les élèves
                </Text>
                <span style={{ color: "red" }}>*</span>
              </HStack>
              <AppInput
                type="file"
                accept=".csv"
                borderWidth="1px"
                alignSelf="stretch"
                borderRadius="8px"
                name="csv"
                id="csv"
                onChange={handleFileChange}
                display={previewSrc ? "flex" : "none"}
              />
              {!previewSrc && (
                <Flex
                  gap="16px"
                  alignSelf="stretch"
                  alignItems="start"
                  flexDirection={{ md: "row", base: "column" }}
                >
                  <Image
                    src={previewSrc ? previewSrc : "images/csv_import_preview.png"}
                    alt="CSV preview"
                    h="80px"
                    alignSelf="center"
                    w={{ md: "25%", base: "100%" }}
                    fit="cover"
                    borderRadius="12px"
                    onClick={() => document.getElementById("csv")?.click()}
                  />
                </Flex>
              )}
              {touched.csv && errors.csv && (
                <Text fontSize="sm" color="red.500" mt={1}>
                  {errors.csv as string}
                </Text>
              )}
            </Flex>

            {!user?.user?.institution_id && (
              <Flex gap="4px" flexDirection="column" alignItems="start">
                <HStack>
                  <Text color="gray_description">Institution</Text>
                  <span style={{ color: "red" }}>*</span>
                </HStack>
                <Flex
                  gap="4px"
                  flexDirection="column"
                  alignItems="start"
                  width="100%"
                >
                  <AppDropdownSelect
                    name="institution_id"
                    data={institutions}
                    value={values.institution_id}
                    onChange={setFieldValue}
                    placeholder="Veuillez sélectionner une institution"
                  />
                  {touched.institution_id && errors.institution_id && (
                    <Text fontSize="sm" color="red.500" mt={1}>
                      {errors.institution_id as string}
                    </Text>
                  )}
                </Flex>
              </Flex>
            )}
          </Flex>

          <Flex mt="24px" gap="12px">
            <Button
              size="4xl"
              color="gray_description"
              fontFamily="Arimo"
              fontWeight={700}
              minW="106px"
              borderRadius="8px"
              type="button"
              onClick={() => navigate("/tracking/students")}
            >
              Annuler
            </Button>
            <Button
              size="4xl"
              colorScheme="red_origin"
              fontFamily="Arimo"
              fontWeight={700}
              minW="132px"
              borderRadius="8px"
              type="submit"
              isLoading={isSubmitting}
            >
              Enregistrer
            </Button>
          </Flex>
        </Flex>
      </form>
    </TrackingPageLayout>
  );
}

